<script setup lang="ts">
import { computed } from 'vue';
import { CLASS_LABELS, xpProgress, type HeroProfile, type EffectiveStats } from '../game/hero-api';
import type { Inventory, Artifact, EquipSlot } from '../game/inventory-api';
import { classArt, itemThumb } from '../game/art';
import { SLOTS } from '../game/inventory-api';
import { STAT_META, SLOT_LABELS } from '../game/item-math';
import { useRegenClock, fmtEta } from '../game/regen-clock';
import QuickRecover from './QuickRecover.vue';

const props = defineProps<{ hero: HeroProfile; stats: EffectiveStats | null; inventory: Inventory | null }>();
const emit = defineEmits<{
  (e: 'inspect', artifact: Artifact, slot: EquipSlot): void;
  (e: 'filter-request', slot: EquipSlot): void;
  (e: 'updated'): void;
}>();

/** Class → aura colour (radial glow behind the portrait). */
const AURA: Record<string, string> = {
  WARRIOR: '#a4342f', PALADIN: '#3a5c86', ARCHER: '#4f8a56', MAGE: '#7a5a9c',
};
const aura = computed(() => AURA[props.hero.hero.class] ?? '#a4342f');

const hpMax = computed(() => props.stats?.effective.maxHp ?? props.hero.hero.stats.maxHp ?? 1);
const hpPct = computed(() => Math.max(0, Math.round((props.hero.hero.stats.hp / hpMax.value) * 100)));
const nervePct = computed(() =>
  Math.max(0, Math.round((props.hero.hero.nerve.current / Math.max(1, props.hero.hero.nerve.max)) * 100)));
const xp = computed(() => xpProgress(props.hero.progression.xp));
const xpPct = computed(() => Math.round((xp.value.current / xp.value.needed) * 100));

// Regen countdowns (client-ticked from the profile's fractional anchors).
const hpClock = useRegenClock(() =>
  ({ anchor: props.hero.hero.regen?.hp, max: hpMax.value }));
const nerveClock = useRegenClock(() =>
  ({ anchor: props.hero.hero.regen?.nerve, max: props.hero.hero.nerve.max }));

const statRows = computed(() => {
  const eff = props.stats?.effective; const gear = props.stats?.gear; const h = props.hero;
  const rows = (['attack', 'defense', 'magic', 'speed'] as const).map((k) => ({
    key: k,
    meta: STAT_META[k],
    val: eff?.[k] ?? h.hero.stats[k],
    gear: k === 'speed' ? (gear?.speed ?? 0) : (gear?.[k] ?? 0),
  }));
  const max = Math.max(...rows.map((r) => r.val), 1);
  return rows.map((r) => ({ ...r, pct: Math.max(6, Math.round((r.val / max) * 100)) }));
});

/** Active boosts (moved here from the dossier) — only the ones currently live. */
const activeBoosts = computed(() => {
  const b = props.stats?.boosts;
  if (!b) return [];
  const out: { label: string; color: string }[] = [];
  if (b.cashRush) out.push({ label: 'Cash Rush ×1.5', color: '#c9a84c' });
  if (b.xpBooster) out.push({ label: 'XP Booster ×1.5', color: '#c9a84c' });
  if (b.revive) out.push({ label: 'Revive ready', color: '#6f9e54' });
  return out;
});

function equippedIn(slot: EquipSlot): Artifact | null {
  return props.inventory?.equipped[slot] ?? null;
}
function onSlotClick(slot: EquipSlot) {
  const a = equippedIn(slot);
  if (a) emit('inspect', a, slot);
  else emit('filter-request', slot);
}
</script>

<template>
  <div class="hc corner-frame relative rounded-xl overflow-hidden border border-border bg-surface h-full flex flex-col">
    <span class="corner-bracket corner-tl"></span>
    <span class="corner-bracket corner-tr"></span>
    <span class="corner-bracket corner-bl"></span>
    <span class="corner-bracket corner-br"></span>

    <!-- portrait — flexes with the viewport when the profile is height-locked -->
    <div class="relative overflow-hidden flex-1 basis-44 min-h-32 max-h-56" :style="{ background: `radial-gradient(circle at 50% 30%, color-mix(in srgb, ${aura} 55%, transparent), transparent 70%)` }">
      <!-- the portrait fills the pane by INSETS, not by percentage height: as a
           centred grid item with h-full, WebKit sized the auto row to the art's
           intrinsic aspect height (388px wide → 519px tall) but resolved the
           img's 100% against the pane (176px), then centred that 176px box in
           the 519px row — pushing the whole portrait past the pane's
           overflow-hidden edge, i.e. a blank block on iOS Safari. Blink
           resolved 100% against the row instead, so Chrome looked fine.
           Absolute inset-0 is definite in every engine; cover+top crops the
           same pixels Chrome was already showing. -->
      <div class="hc-breathe absolute inset-0">
        <img v-if="classArt(hero.hero.class)" :src="classArt(hero.hero.class)" :alt="hero.hero.class"
             class="absolute inset-0 w-full h-full object-cover object-top" />
      </div>
      <div class="hc-scanlines absolute inset-0"></div>
      <div class="absolute inset-0" style="background:linear-gradient(180deg,rgba(10,7,20,0) 40%,rgba(10,7,20,0.55) 75%,rgba(10,7,20,0.96) 100%)"></div>

      <span class="hc-level">Lv {{ hero.progression.level }}</span>

      <div class="absolute inset-x-3 bottom-2.5">
        <div class="font-label uppercase tracking-[0.2em] text-[10px] text-gold/80">{{ CLASS_LABELS[hero.hero.class] }}</div>
        <h1 class="font-heading text-2xl text-gold leading-tight truncate">{{ hero.hero.name }}</h1>
      </div>
    </div>

    <div class="p-4 shrink-0">
      <!-- combat stat bars -->
      <div class="flex flex-col gap-2.5 mb-4">
        <div v-for="r in statRows" :key="r.key" class="flex items-center gap-2.5">
          <span class="w-16 shrink-0 font-label uppercase tracking-wider text-[10px] text-text-dim leading-none">{{ r.meta.short }}</span>
          <div class="hc-track flex-1">
            <div class="hc-fill" :style="{ width: r.pct + '%', background: r.meta.color, boxShadow: `0 0 6px ${r.meta.color}` }"></div>
          </div>
          <span class="w-16 text-right font-body tabular-nums text-text text-[13px] leading-none">
            {{ r.val }}<span v-if="r.gear" class="text-gold text-[10px]"> +{{ r.gear }}</span>
          </span>
        </div>
      </div>

      <!-- active boosts (moved from the dossier) -->
      <div v-if="activeBoosts.length" class="mb-4">
        <div class="hc-h mb-1.5">Active boosts</div>
        <div class="flex flex-wrap gap-1.5">
          <span v-for="bst in activeBoosts" :key="bst.label" class="hc-boost" :style="{ '--c': bst.color }">{{ bst.label }}</span>
        </div>
      </div>

      <!-- equipped strip -->
      <div class="hc-h mb-1.5">Equipped</div>
      <div class="grid grid-cols-6 gap-1.5 mb-4">
        <button
          v-for="slot in SLOTS" :key="slot"
          class="hc-slot"
          :style="{ '--rar': equippedIn(slot) ? '#c8a24c' : 'transparent' }"
          :title="equippedIn(slot)?.name ?? `${SLOT_LABELS[slot]} — empty`"
          @click="onSlotClick(slot)"
        >
          <img :src="itemThumb(slot.toUpperCase(), equippedIn(slot)?.rarity, equippedIn(slot)?.name)" :alt="slot"
               class="w-full h-full object-cover rounded" :class="{ 'opacity-30': !equippedIn(slot) }" />
        </button>
      </div>

      <!-- HP -->
      <div class="mb-2.5">
        <div class="flex items-center justify-between mb-1">
          <span class="hc-h leading-none">Health<span v-if="hpClock && !hpClock.full" class="hc-eta" :title="`+5 HP a minute — back to full in ${fmtEta(hpClock.fullIn)}`">full in {{ fmtEta(hpClock.fullIn) }}</span></span>
          <span class="font-body tabular-nums text-xs text-text leading-none">{{ hero.hero.stats.hp }} / {{ hpMax }}</span>
        </div>
        <div class="hc-track hc-track-shimmer">
          <div class="hc-fill" :style="{ width: hpPct + '%', background: hpPct > 50 ? 'var(--color-success)' : hpPct > 25 ? '#d9a441' : 'var(--color-error)' }"></div>
        </div>
      </div>

      <!-- XP -->
      <div class="mb-2.5">
        <div class="flex items-center justify-between mb-1">
          <span class="hc-h leading-none">Level {{ hero.progression.level }}</span>
          <span class="font-body tabular-nums text-xs text-text-dim leading-none">{{ xp.current }} / {{ xp.needed }}</span>
        </div>
        <div class="hc-track hc-track-shimmer">
          <div class="hc-fill" :style="{ width: xpPct + '%', background: 'var(--color-purple-light)' }"></div>
        </div>
      </div>

      <!-- Nerve -->
      <div>
        <div class="flex items-center justify-between mb-1">
          <span class="hc-h leading-none">Nerve<span v-if="nerveClock && !nerveClock.full" class="hc-eta" :title="`+1 nerve every 2.5 minutes — next in ${fmtEta(nerveClock.nextIn)}`">+1 in {{ fmtEta(nerveClock.nextIn) }}</span></span>
          <span class="font-body tabular-nums text-xs text-text leading-none">{{ hero.hero.nerve.current }} / {{ hero.hero.nerve.max }}</span>
        </div>
        <div class="hc-track">
          <div class="hc-fill" :style="{ width: nervePct + '%', background: 'var(--color-gold)' }"></div>
        </div>
      </div>

      <!-- running on fumes? one-tap recovery right under the gauges -->
      <QuickRecover
        class="mt-3"
        :hp="hero.hero.stats.hp" :max-hp="hpMax" :full-hp="stats?.base.maxHp"
        :hp-regen="hero.hero.regen?.hp" :nerve-regen="hero.hero.regen?.nerve"
        :nerve="hero.hero.nerve.current" :max-nerve="hero.hero.nerve.max"
        :cash="hero.progression.cash" :gold-bars="hero.progression.goldBars"
        @updated="emit('updated')"
      />
    </div>
  </div>
</template>

<style scoped>
.hc-h {
  font-family: var(--font-label, sans-serif);
  text-transform: uppercase; letter-spacing: 0.14em;
  font-size: 10.5px; color: var(--color-text-dim, #8a8378);
}
/* regen ETA rides the row label, not the numbers — quiet, lowercase, body face */
.hc-eta {
  font-family: var(--font-body, serif);
  text-transform: none; letter-spacing: 0.02em;
  font-size: 10.5px; font-variant-numeric: tabular-nums;
  color: color-mix(in srgb, var(--color-text-dim, #8a8378) 75%, transparent);
  margin-left: 7px;
}
.hc-level {
  position: absolute; top: 10px; right: 10px;
  font-family: var(--font-label, sans-serif); font-weight: 600;
  font-size: 11px; letter-spacing: 0.08em;
  color: var(--color-bg, #0a0714);
  background: var(--color-gold-bright, #e6c15a);
  border-radius: 999px; padding: 3px 10px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.4);
}
.hc-breathe { animation: hc-breathe 5s ease-in-out infinite; }
@keyframes hc-breathe {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.025); }
}
.hc-scanlines {
  background: repeating-linear-gradient(180deg, rgba(255,255,255,0.025) 0px, rgba(255,255,255,0.025) 1px, transparent 1px, transparent 3px);
  mix-blend-mode: overlay;
  pointer-events: none;
}
.hc-track {
  height: 7px; border-radius: 999px;
  background: rgba(0,0,0,0.45);
  border: 1px solid rgba(0,0,0,0.4);
  overflow: hidden;
  position: relative;
}
.hc-fill {
  height: 100%; border-radius: 999px;
  transition: width 800ms ease;
  position: relative;
}
.hc-fill::after {
  content: ''; position: absolute; inset: 0 0 50% 0;
  background: linear-gradient(180deg, rgba(255,255,255,0.35), transparent);
}
.hc-track-shimmer .hc-fill::before {
  content: '';
  position: absolute; top: 0; bottom: 0; width: 40%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent);
  animation: hc-shimmer 2.4s ease-in-out infinite;
}
@keyframes hc-shimmer {
  0% { left: -40%; }
  100% { left: 110%; }
}
.hc-slot {
  aspect-ratio: 1; border-radius: 6px; padding: 2px;
  border: 1px solid color-mix(in srgb, var(--rar, transparent) 55%, var(--color-border, #2a2436));
  background: rgba(0,0,0,0.25);
  transition: border-color 130ms ease, box-shadow 130ms ease;
}
.hc-slot:hover { border-color: var(--color-gold, #c9a84c); box-shadow: 0 0 8px color-mix(in srgb, var(--color-gold, #c9a84c) 45%, transparent); }
.hc-boost {
  font-family: var(--font-label, sans-serif);
  text-transform: uppercase; letter-spacing: 0.08em;
  font-size: 9.5px; padding: 3px 9px; border-radius: 999px;
  color: var(--c); border: 1px solid color-mix(in srgb, var(--c) 55%, transparent);
  background: color-mix(in srgb, var(--c) 12%, transparent);
}

@media (prefers-reduced-motion: reduce) {
  .hc-breathe { animation: none; }
  .hc-track-shimmer .hc-fill::before { animation: none; display: none; }
}
</style>
